import { useState } from 'react';
import { 
  FaImage, 
  FaPlus, 
  FaTrash, 
  FaGlobe,
  FaDesktop,
  FaMobile,
  FaTabletAlt
} from 'react-icons/fa';
import { 
  IoCheckmarkCircle,
} from 'react-icons/io5';

const GallerySelection = ({ 
  images, 
  onUpdateImage,
  getImagesBySection
}) => {
  const [activeSection, setActiveSection] = useState('hero');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [draggedImage, setDraggedImage] = useState(null);

  // Updated to match actual landing page structure
  const landingSections = [
    {
      id: 'hero',
      name: 'Hero Section',
      description: 'Main banner background image - Single hero image',
      maxImages: 1,
      recommendations: ['High-resolution landscape images (1920x1080+)', 'Strong visual impact', 'Professional quality'],
      dimensions: '1920x1080 or larger recommended'
    },
    {
      id: 'about',
      name: 'About Section', 
      description: 'About us grid - Team and company story',
      maxImages: 4,
      recommendations: ['Landscaping work photos', 'Garden designs', 'Professional work', 'Behind-the-scenes'],
      dimensions: '800x600 recommended'
    },
    {
      id: 'gallery',
      name: 'Portfolio Gallery',
      description: 'Main project showcase with category filtering',
      maxImages: 50, 
      recommendations: ['Best completed projects', 'Diverse work samples', 'High visual impact', 'Different project types'],
      dimensions: '800x600 recommended',
      categories: ['Garden Design', 'Full Landscape', 'Maintenance', 'Seasonal', 'Hardscape', 'Planting'] // Added actual categories
    }
  ];

  // Helper functions
  const getAvailableImages = () => {
    return images.filter(img => !img.onLandingPage && img.isActive);
  };

  const getSectionStatus = (sectionId) => {
    const sectionImages = getImagesBySection ? getImagesBySection(sectionId) : [];
    const section = landingSections.find(s => s.id === sectionId);
    
    if (sectionImages.length === 0) {
      return { status: 'empty', message: 'No images assigned', color: 'text-gray-500' };
    } else if (sectionImages.length >= section.maxImages) {
      return { status: 'full', message: 'Complete', color: 'text-green-600' };
    } else {
      return { status: 'partial', message: `${sectionImages.length}/${section.maxImages} images`, color: 'text-blue-600' };
    }
  };

  const handleDragStart = (e, image) => {
    setDraggedImage(image);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, section) => {
    e.preventDefault();
    if (draggedImage) {
      const sectionImages = getImagesBySection ? getImagesBySection(section) : [];
      const sectionData = landingSections.find(s => s.id === section);
      
      if (sectionImages.length < sectionData.maxImages) {
        onUpdateImage(draggedImage.id, { 
          onLandingPage: true, 
          landingSection: section 
        });
      }
      setDraggedImage(null);
    }
  };

  const handleRemoveFromSection = (imageId) => {
    onUpdateImage(imageId, { 
      onLandingPage: false, 
      landingSection: null 
    });
  };

  const activeSection_data = landingSections.find(s => s.id === activeSection);
  const sectionImages = getImagesBySection ? getImagesBySection(activeSection) : [];
  const availableImages = getAvailableImages();
  const sectionStatus = getSectionStatus(activeSection);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Landing Page Gallery</h2>
          <p className="text-gray-600">Assign images to different sections of your landing page</p>
        </div>
      </div>

      {/* Section Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {landingSections.map((section) => {
          const sectionImages = getImagesBySection ? getImagesBySection(section.id) : [];
          const status = getSectionStatus(section.id);
          
          return (
            <div
              key={section.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                activeSection === section.id 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setActiveSection(section.id)}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{section.name}</h4>
                {status.status === 'full' && <IoCheckmarkCircle className="w-4 h-4 text-green-500" />}
              </div>
              <div className={`text-sm ${status.color} mb-2`}>
                {status.message}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${(sectionImages.length / section.maxImages) * 100}%` }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Section Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {landingSections.map((section) => {
              const status = getSectionStatus(section.id);
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeSection === section.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span>{section.name}</span>
                  <span className={`text-xs ${status.color}`}>
                    ({getImagesBySection ? getImagesBySection(section.id).length : 0})
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Section Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Section Info & Assigned Images */}
        <div className="lg:col-span-2 space-y-6">
          {/* Section Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{activeSection_data.name}</h3>
                <p className="text-gray-600 mt-1">{activeSection_data.description}</p>
              </div>
              <div className="text-right">
                <div className={`text-sm font-medium ${sectionStatus.color}`}>
                  {sectionStatus.message}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Max: {activeSection_data.maxImages} image{activeSection_data.maxImages !== 1 ? 's' : ''}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Recommendations:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {activeSection_data.recommendations.map((rec, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-medium">Optimal dimensions:</span> {activeSection_data.dimensions}
              </div>
              {activeSection_data.categories && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Available categories:</span> {activeSection_data.categories.join(', ')}
                </div>
              )}
            </div>
          </div>

          {/* Assigned Images */}
          <div 
            className="bg-white rounded-lg shadow p-6"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, activeSection)}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Assigned Images ({sectionImages.length}/{activeSection_data.maxImages})
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setPreviewMode('desktop')}
                  className={`p-2 rounded ${previewMode === 'desktop' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  <FaDesktop className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPreviewMode('tablet')}
                  className={`p-2 rounded ${previewMode === 'tablet' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  <FaTabletAlt className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPreviewMode('mobile')}
                  className={`p-2 rounded ${previewMode === 'mobile' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  <FaMobile className="w-4 h-4" />
                </button>
              </div>
            </div>

            {sectionImages.length === 0 ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <FaImage className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">No images assigned to this section</p>
                <p className="text-sm text-gray-400">Drag images from the available pool or upload new ones</p>
              </div>
            ) : (
              <div className={`grid gap-4 ${
                previewMode === 'desktop' ? 'grid-cols-3' :
                previewMode === 'tablet' ? 'grid-cols-2' :
                'grid-cols-1'
              }`}>
                {sectionImages.map((image, index) => (
                  <div key={image.id} className="relative group">
                    <img
                      src={image.thumbnail || image.url}
                      alt={image.alt || image.title}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded-lg flex items-center justify-center">
                      <button
                        onClick={() => handleRemoveFromSection(image.id)}
                        className="opacity-0 group-hover:opacity-100 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-all"
                      >
                        <FaTrash className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="absolute top-2 left-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                      {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Available Images Pool */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Images</h3>
            
            {availableImages.length === 0 ? (
              <div className="text-center py-8">
                <FaImage className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No available images</p>
                <p className="text-gray-400 text-xs">Upload images or remove them from other sections</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                {availableImages.map((image) => (
                  <div
                    key={image.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, image)}
                    className="relative group cursor-move border-2 border-transparent hover:border-blue-400 rounded-lg transition-all"
                  >
                    <img
                      src={image.thumbnail || image.url}
                      alt={image.alt || image.title}
                      className="w-full h-20 object-cover rounded-md"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-md flex items-center justify-center">
                      <FaPlus className="text-white opacity-0 group-hover:opacity-100" />
                    </div>
                    <div className="absolute bottom-1 left-1 right-1">
                      <div className="bg-black bg-opacity-75 text-white text-xs px-1 py-0.5 rounded truncate">
                        {image.title}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Summary Stats */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <FaGlobe className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-blue-900">Landing Page Summary</span>
            </div>
            <div className="space-y-1 text-sm text-gray-600">
              <div>Total sections: {landingSections.length}</div>
              <div>Total assigned: {images.filter(img => img.onLandingPage).length} images</div>
              <div>Available: {availableImages.length} images</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GallerySelection;